import PropTypes from "prop-types";
import { Button, Grid, Typography } from "@mui/material";
import { useForm } from "react-hook-form";
import { useEffect, useMemo } from "react";
import {
  anredeArray,
  bezahltBisArray,
  fullMemberFormData,
  getNewMemberPayedTillValue,
  gradArray,
} from "../../../components/form/fields/inputFields";
import TextField from "../../../components/form/fields/TextField";
import SelectField from "../../../components/form/fields/SelectField";
import DEDateField from "../../../components/form/fields/DEDateField";
import { getDateFromDateField } from "../../../utils/dataUtils";
import BeltAvatar from "../../../components/beltavatar/BeltAvatar";

export default function PublicMemberForm({
  memberData,
  defaults,
  onSubmit,
  readOnlyFields,
  contractDurationLabel,
}) {
  const { handleSubmit, control, watch, setValue } = useForm({
    defaultValues: {
      title: "",
      lastName: "",
      firstName: "",
      birthdate: new Date(),
      bodyHeightCm: "",
      contractDate: new Date(),
      comment: "",
      degree: 0,
      payRegistrationFee: true,
      payedTill: getNewMemberPayedTillValue(),
      ...defaults,
      ...memberData,
    },
  });

  const degree = watch("degree");
  const memberFormFields = useMemo(
    () =>
      fullMemberFormData.map((field) => {
        if (field.value !== "contractDuration" || !contractDurationLabel) {
          return field;
        }
        return {
          ...field,
          label: contractDurationLabel,
          errorMsg: "Bitte die Mindestvertragslaufzeit auswählen.",
        };
      }),
    [contractDurationLabel],
  );

  const onSubmitForm = (data) => {
    const parsedBodyHeightCm = Number(data?.bodyHeightCm);
    onSubmit({
      ...data,
      bodyHeightCm: Number.isFinite(parsedBodyHeightCm)
        ? Math.round(parsedBodyHeightCm)
        : data?.bodyHeightCm,
      birthdate: getDateFromDateField(data?.birthdate),
      contractDate: getDateFromDateField(data?.contractDate),
      beginningDate: getDateFromDateField(data?.beginningDate),
      lastTesting: getDateFromDateField(data?.lastTesting),
    });
  };

  const isReadOnly = (field) => readOnlyFields?.includes(field);

  useEffect(() => {
    if (!defaults || typeof defaults !== "object") return;
    const syncFields = [
      "memberNumber",
      "contribution",
      "contractDuration",
      "contractExtension",
      "noticePeriod",
      "registrationFee",
      "payedTill",
    ];
    syncFields.forEach((field) => {
      if (Object.prototype.hasOwnProperty.call(defaults, field)) {
        setValue(field, defaults[field]);
      }
    });
  }, [defaults, setValue]);

  const renderSiteInputFields = (fields) => {
    return fields.map((field) => {
      const disabled = isReadOnly(field.value);
      if (field.type === "date") {
        return (
          <Grid key={field.value} item xs={12} md={field.size || 4}>
            <DEDateField
              control={control}
              name={field.value}
              label={field.label}
              required={field.required}
              errorMsg={field.errorMsg}
              helperText={field.helperText}
              disabled={disabled}
            />
          </Grid>
        );
      }

      return (
        <Grid key={field.value} item xs={12} md={field.size || 4}>
          <TextField
            control={control}
            name={field.value}
            label={field.label}
            type={field.type}
            variant="outlined"
            required={field.required}
            errorMsg={field.errorMsg}
            helperText={field.helperText}
            fullWidth
            multiline={field?.multiline}
            endAdornment={field?.endAdornment}
            disabled={disabled}
          />
        </Grid>
      );
    });
  };

  return (
    <form onSubmit={handleSubmit(onSubmitForm)} className="form">
      <Grid container spacing={2} alignItems="center" style={{ paddingTop: 5 }}>
        <Grid item xs={12}>
          <Typography variant="h6">Mitgliedsdaten</Typography>
        </Grid>
        <Grid item xs={12}>
          <SelectField
            style={{ minWidth: 180 }}
            control={control}
            name="title"
            value="title"
            label="Anrede"
            variant="outlined"
            required
            itemArray={anredeArray}
          />
        </Grid>
        {renderSiteInputFields(memberFormFields.slice(1, 4))}
        <Grid item xs={12} md={4}>
          <TextField
            control={control}
            name="bodyHeightCm"
            label="Körpergröße (ca. in cm)"
            type="number"
            variant="outlined"
            required="Bitte die ungefähre Körpergröße eingeben."
            helperText="Diese Angabe hilft uns, den passenden Anzug vorzubereiten."
            rules={{
              validate: (value) =>
                String(value ?? "").trim() !== "" ||
                "Bitte die ungefähre Körpergröße eingeben.",
              min: {
                value: 80,
                message: "Bitte einen realistischen Wert ab 80 cm eingeben.",
              },
              max: {
                value: 250,
                message: "Bitte einen realistischen Wert bis 250 cm eingeben.",
              },
            }}
            endAdornment="cm"
            fullWidth
          />
        </Grid>

        <Grid item xs={12}>
          <Typography variant="h6">Vertragsdaten</Typography>
        </Grid>
        {renderSiteInputFields(memberFormFields.slice(4, 10))}
        <Grid item xs={12} md={4}>
          <SelectField
            style={{ minWidth: 180 }}
            control={control}
            name="payedTill"
            value="payedTill"
            label="Bezahlt bis"
            variant="outlined"
            required
            itemArray={bezahltBisArray}
            helperText="Bis welchem Monat wurde der Beitrag bereits gezahlt?"
            disabled={isReadOnly("payedTill")}
          />
        </Grid>
        {renderSiteInputFields(memberFormFields.slice(10, 11))}
        <Grid item xs={12} md={6}>
          <Typography variant="body1">
            Anmeldegebühr wird einmalig eingezogen: Ja
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <Typography variant="h6">Zusatz</Typography>
        </Grid>
        <Grid item xs={12} md={2}>
          <SelectField
            style={{ minWidth: 180 }}
            control={control}
            name="degree"
            value="degree"
            label="Gürtelgrad"
            variant="outlined"
            required
            itemArray={gradArray}
            disabled={isReadOnly("degree")}
          />
        </Grid>
        <Grid alignContent={"center"} item xs={12} md={2}>
          <BeltAvatar beltNumber={degree} size="medium" />
        </Grid>
        {renderSiteInputFields(memberFormFields.slice(12, 13))}
        {renderSiteInputFields(memberFormFields.slice(13, 16))}
      </Grid>
      <Button
        type="submit"
        fullWidth
        variant="contained"
        color="secondary"
        sx={{
          mt: 2,
          mb: 3,
          fontSize: "125%",
        }}
      >
        Weiter
      </Button>
    </form>
  );
}

PublicMemberForm.propTypes = {
  memberData: PropTypes.object,
  defaults: PropTypes.object,
  onSubmit: PropTypes.func.isRequired,
  readOnlyFields: PropTypes.arrayOf(PropTypes.string),
  contractDurationLabel: PropTypes.string,
};

PublicMemberForm.defaultProps = {
  memberData: {},
  defaults: {},
  readOnlyFields: [],
  contractDurationLabel: "",
};
